import { create } from "zustand";
import { createJSONStorage, persist } from "zustand/middleware";

import type { Annotation } from "@/types/annotation";

export type AttachmentAnnotationDraftScope = {
  tenantKey: string;
  userId: string;
  attachmentId: string;
};

export type AttachmentAnnotationDraft = {
  annotations: Annotation[];
  baseline: Annotation[];
  updatedAt: number;
};

type AttachmentAnnotationDraftStore = {
  drafts: Record<string, AttachmentAnnotationDraft>;
  getScopeKey: (scope: AttachmentAnnotationDraftScope) => string;
  setDraft: (
    scope: AttachmentAnnotationDraftScope,
    annotations: Annotation[],
    baseline: Annotation[],
  ) => void;
  clearScope: (scope: AttachmentAnnotationDraftScope) => void;
  clearTenant: (tenantKey: string) => void;
};

const scopeKey = (scope: AttachmentAnnotationDraftScope) =>
  `${scope.tenantKey}:${scope.userId}:${scope.attachmentId}`;

export const useAttachmentAnnotationDraftStore =
  create<AttachmentAnnotationDraftStore>()(
    persist(
      (set) => ({
        drafts: {},
        getScopeKey: scopeKey,
        setDraft: (scope, annotations, baseline) =>
          set((state) => ({
            drafts: {
              ...state.drafts,
              [scopeKey(scope)]: {
                annotations,
                baseline,
                updatedAt: Date.now(),
              },
            },
          })),
        clearScope: (scope) =>
          set((state) => {
            const { [scopeKey(scope)]: _cleared, ...drafts } = state.drafts;
            return { drafts };
          }),
        clearTenant: (tenantKey) =>
          set((state) => ({
            drafts: Object.fromEntries(
              Object.entries(state.drafts).filter(
                ([key]) => !key.startsWith(`${tenantKey}:`),
              ),
            ),
          })),
      }),
      {
        name: "attachment-annotation-draft-store",
        storage: createJSONStorage(() => sessionStorage),
        partialize: (state) =>
          ({ drafts: state.drafts }) as AttachmentAnnotationDraftStore,
        version: 1,
      },
    ),
  );
